import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
export type EchoImportJob = {
  id: string;
  status: string;
  name: string;
  map: string;
  error?: string;
};
const open = (job: EchoImportJob) =>
  !["completed", "failed"].includes(job.status);
const API =
  (import.meta.env.VITE_API_URL || "http://localhost:3001") +
  "/game-server/training/echo-imports";
const EchoImportsContext = createContext({
  jobs: [] as EchoImportJob[],
  unavailable: false,
});
export const useEchoImports = () => useContext(EchoImportsContext);
export function EchoImportsProvider({ children }: { children: ReactNode }) {
  const { user, token } = useAuth();
  return (
    <ScopedEchoImports key={`${user?.id}:${user?.teamId}:${token}`}>
      {children}
    </ScopedEchoImports>
  );
}
function ScopedEchoImports({ children }: { children: ReactNode }) {
  const { user, token } = useAuth();
  const [jobs, setJobs] = useState<EchoImportJob[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const teamId = user?.teamId;
  useEffect(() => {
    setJobs([]);
    setUnavailable(false);
    if (!token || !teamId) return;
    let disposed = false,
      busy = false;
    let submissionVersion = 0;
    let pollAgain = false;
    const known = new Map<string, string>();
    const key = "echo-import-dismissed:" + user.id + ":" + teamId;
    const dismissed = new Set<string>();
    try {
      for (const id of JSON.parse(localStorage.getItem(key) || "[]"))
        dismissed.add(id);
    } catch {}
    async function poll() {
      if (busy || disposed) return;
      busy = true;
      const version = submissionVersion;
      try {
        const response = await axios.get<EchoImportJob[]>(API, {
          headers: { Authorization: "Bearer " + token },
          timeout: 15000,
        });
        if (disposed) return;
        if (version !== submissionVersion) {
          pollAgain = true;
          return;
        }
        setUnavailable(false);
        for (const job of response.data) {
          if (
            known.has(job.id) &&
            known.get(job.id) !== job.status &&
            !open(job)
          ) {
            if (job.status === "completed") {
              toast.success(job.name + " added to your Echo library.");
              window.dispatchEvent(
                new CustomEvent("echo-import-completed", {
                  detail: { teamId, map: job.map },
                }),
              );
            } else
              toast.error(job.name + ": " + (job.error || "Import failed."));
          }
          known.set(job.id, job.status);
        }
        setJobs(
          response.data.filter((job) => open(job) || !dismissed.has(job.id)),
        );
      } catch {
        if (!disposed) setUnavailable(true);
      } finally {
        busy = false;
        if (pollAgain && !disposed) {
          pollAgain = false;
          void poll();
        }
      }
    }
    const submitted = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.teamId !== teamId || detail?.userId !== user?.id) return;
      const job = detail.job as EchoImportJob;
      submissionVersion++;
      known.set(job.id, job.status);
      setJobs((rows) => [job, ...rows.filter((row) => row.id !== job.id)]);
      if (busy) pollAgain = true;
      void poll();
    };
    const dismiss = (event: Event) => {
      const id = (event as CustomEvent<string>).detail;
      dismissed.add(id);
      try {
        localStorage.setItem(key, JSON.stringify([...dismissed].slice(-100)));
      } catch {}
      setJobs((rows) => rows.filter((row) => row.id !== id));
    };
    window.addEventListener("echo-import-submitted", submitted);
    window.addEventListener("echo-import-dismiss", dismiss);
    void poll();
    const timer = setInterval(() => void poll(), 5000);
    return () => {
      disposed = true;
      clearInterval(timer);
      window.removeEventListener("echo-import-submitted", submitted);
      window.removeEventListener("echo-import-dismiss", dismiss);
    };
  }, [token, teamId, user?.id]);
  return (
    <EchoImportsContext.Provider value={{ jobs, unavailable }}>
      {children}
    </EchoImportsContext.Provider>
  );
}
